import { Tabs as TabsPrimitive } from 'radix-ui';
import type { ComponentProps } from 'react';

import { cn } from '@/shared/lib/cn';

export function Tabs({ className, ...props }: ComponentProps<typeof TabsPrimitive.Root>) {
  return <TabsPrimitive.Root data-slot="tabs" className={cn('flex flex-col gap-3', className)} {...props} />;
}

/**
 * `segmented` – pill group on a muted track (filters, view switchers);
 * `underline` – page-level tabs with an accent underline.
 */
export function TabsList({
  className,
  variant = 'segmented',
  ...props
}: ComponentProps<typeof TabsPrimitive.List> & { variant?: 'segmented' | 'underline' }) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      className={cn(
        'group/tabs inline-flex w-fit items-center text-muted-foreground',
        variant === 'segmented' && 'h-9 rounded-lg bg-muted p-[3px]',
        variant === 'underline' && 'h-10 gap-4 border-b',
        className,
      )}
      {...props}
    />
  );
}

/** The look of a tab (both variants); other components that lay out tabs of their own (a hidden mirror that measures them) reuse it. */
export const tabsTriggerClass = cn(
        "inline-flex items-center justify-center gap-1.5 text-sm font-medium whitespace-nowrap focus-ring transition-[color,background-color,box-shadow] outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        'group-data-[variant=segmented]/tabs:h-full group-data-[variant=segmented]/tabs:flex-1 group-data-[variant=segmented]/tabs:rounded-md group-data-[variant=segmented]/tabs:px-3 group-data-[variant=segmented]/tabs:hover:text-foreground group-data-[variant=segmented]/tabs:data-[state=active]:bg-background group-data-[variant=segmented]/tabs:data-[state=active]:text-foreground group-data-[variant=segmented]/tabs:data-[state=active]:shadow-sm dark:group-data-[variant=segmented]/tabs:data-[state=active]:bg-accent',
        'group-data-[variant=underline]/tabs:relative group-data-[variant=underline]/tabs:h-full group-data-[variant=underline]/tabs:px-0.5 group-data-[variant=underline]/tabs:after:absolute group-data-[variant=underline]/tabs:after:inset-x-0 group-data-[variant=underline]/tabs:after:-bottom-px group-data-[variant=underline]/tabs:after:h-0.5 group-data-[variant=underline]/tabs:after:rounded-full group-data-[variant=underline]/tabs:hover:text-foreground group-data-[variant=underline]/tabs:data-[state=active]:text-foreground group-data-[variant=underline]/tabs:data-[state=active]:after:bg-brand',
);

export function TabsTrigger({ className, ...props }: ComponentProps<typeof TabsPrimitive.Trigger>) {
  return <TabsPrimitive.Trigger data-slot="tabs-trigger" className={cn(tabsTriggerClass, className)} {...props} />;
}

export function TabsContent({ className, ...props }: ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn('flex-1 outline-none', className)}
      {...props}
    />
  );
}
